import { orgQueryKeys } from '@/config/queryKeys';
import type { DownloadedFile } from '@/lib/api-client';
import { apiClient } from '@/lib/api-client';
import type {
  DateFieldFilters,
  NumericFieldFilters,
  OrgCreate,
  OrgPublic,
  OrgSortField,
  OrgUpdate,
  SortOrder,
  TextFieldFilters,
} from '@/types';

/**
 * Creates a new organization using OpenAPI OrgCreate.
 * @param payload OrgCreate (OpenAPI)
 * @returns Promise resolving to OrgPublic
 */
export async function createOrganization(payload: OrgCreate): Promise<OrgPublic> {
  const result = await apiClient.post<OrgPublic>(orgQueryKeys.endpoints.list(), payload);
  if (!result) {
    throw new Error('Organization creation failed - no response from server');
  }
  return result;
}

/**
 * Fetches a list of all organizations.
 * GET /orgs → OrgPublic[]
 */
export async function listOrganizations(): Promise<OrgPublic[]> {
  const orgs = await apiClient.get<OrgPublic[]>(orgQueryKeys.endpoints.list());
  if (!orgs) {
    throw new Error('Failed to fetch organizations');
  }
  return orgs;
}

/**
 * Fetches a single organization by orgId.
 * GET /orgs/{org_id} → OrgPublic
 */
export async function getOrganization(orgId: string): Promise<OrgPublic> {
  if (!orgId) throw new Error('No organization ID provided');
  const org = await apiClient.get<OrgPublic>(orgQueryKeys.endpoints.detail(orgId));
  if (!org) throw new Error(`Failed to fetch org ${orgId}`);
  return org;
}

/**
 * Updates an existing organization.
 * PUT /orgs/{org_id} → OrgPublic
 */
export async function updateOrganization(orgId: string, payload: OrgUpdate): Promise<OrgPublic> {
  const result = await apiClient.put<OrgPublic>(orgQueryKeys.endpoints.detail(orgId), payload);
  if (!result) throw new Error('Organization update failed - no response from server');
  return result;
}

// Not a generated schema: orgs have no /search endpoint, so OrgFilters is never a request body
// and FastAPI emits no component for it. Shape matches the backend model exactly.
export interface OrgFilters {
  name?: TextFieldFilters | null;
  alias?: TextFieldFilters | null;
  email_domain?: TextFieldFilters | null;
  address?: TextFieldFilters | null;
  num_studies?: NumericFieldFilters | null;
  created_at?: DateFieldFilters | null;
  updated_at?: DateFieldFilters | null;
}

export interface OrgsDownloadOptions {
  sortBy?: OrgSortField;
  sortOrder?: SortOrder;
  filters?: OrgFilters | null;
}

/** Streams the caller's accessible orgs as CSV, honouring the grid's current sort and filters. */
export async function downloadOrgsCsv(options: OrgsDownloadOptions = {}): Promise<DownloadedFile> {
  const { filters, sortBy, sortOrder } = options;

  return apiClient.download(orgQueryKeys.endpoints.download(), {
    query: {
      sort_by: sortBy,
      sort_order: sortOrder,
      filters: filters ? JSON.stringify(filters) : undefined,
    },
  });
}
